// Hotel results. Same layout language as the flight Results page — a topbar
// summarising the search, a left filter rail, sort tabs, then the list.
//
// Two differences worth knowing:
//
//   • **Filters are client-side, and honestly labelled as such.** The supplier
//     exposes no server-side rate filter, so the price range, board and
//     facility facets describe the page we fetched rather than the whole city.
//     The backend computes them from the priced results (`facets`) so the
//     numbers already carry markup and FX.
//   • **There is no sticky "continue" bar.** A hotel card isn't bookable — the
//     room is. Picking a hotel opens its page, which is where the offers live.

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { loadHotelSearch, saveHotelSearch, type CachedHotelSearch } from "../lib/hotelSearchCache";
import { useCountdown } from "../lib/useCountdown";
import {
  fmtBoard, fmtDateLong, fmtMoney, nightsLabel, occupancyLabel, reviewLabel, starLabel,
} from "../lib/hotelFormat";
import {
  DEFAULT_HOTEL_SOURCE, HOTEL_SOURCES,
  type HotelCard, type HotelSearchResponse, type HotelSource,
} from "../lib/hotelTypes";
import { CountdownPill } from "../components/HoldTimer";
import { ProviderSwitch } from "../components/ProviderSwitch";

type SortMode = "cheapest" | "top_rated" | "best_value";

interface Filters {
  stars: Set<number>;
  boards: Set<string>;
  facilities: Set<string>;
  refundableOnly: boolean;
  breakfastOnly: boolean;
  maxPrice: number | null;
}

// Fallback result lifetime when the backend doesn't state its own.
const RESULT_TTL_MS = 10 * 60 * 1000;

function sourceLabel(source: string): string {
  return HOTEL_SOURCES.find((s) => s.value === source)?.label ?? source;
}

export function HotelResults() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cached = useMemo(() => loadHotelSearch(), []);

  const reSearch = useMutation({
    mutationFn: async (form: CachedHotelSearch["form"]) => {
      const payload: Record<string, unknown> = {
        city_name: form.city_name,
        country_code: form.country_code,
        check_in: form.check_in,
        check_out: form.check_out,
        occupancies: form.occupancies,
        nationality: form.nationality,
        currency: form.currency,
        // Whatever provider the caller asked for. The expired-rates banner
        // passes the form back unchanged so a retry stays on the supplier the
        // partner was already looking at; the source switch passes a form with
        // a different `source`, which is the one case where landing on another
        // upstream is the point.
        source: form.source,
        limit: 60,
      };
      return api.post<HotelSearchResponse>("/v1/hotels/search/", payload);
    },
    onSuccess: (resp, form) => {
      saveHotelSearch({ form, response: resp, fetched_at: Date.now() });
      window.location.reload();
    },
  });

  const [filters, setFilters] = useState<Filters>(() => ({
    stars: new Set(),
    boards: new Set(),
    facilities: new Set(),
    refundableOnly: false,
    // The search page's "Breakfast included" pill can't be pushed to the
    // supplier, so it arrives here as a query param and seeds the filter.
    breakfastOnly: params.get("breakfast") === "1",
    maxPrice: null,
  }));
  const [sort, setSort] = useState<SortMode>("cheapest");

  const resp = cached?.response;
  const hotels = resp?.hotels ?? [];
  const filtered = useMemo(() => applyFilters(hotels, filters), [hotels, filters]);
  const sorted = useMemo(() => sortHotels(filtered, sort), [filtered, sort]);

  const onOpen = useCallback((hotelId: string) => {
    nav(`/hotel/${encodeURIComponent(hotelId)}`);
  }, [nav]);

  const ttlMs = resp?.remaining_seconds != null ? resp.remaining_seconds * 1000 : RESULT_TTL_MS;
  const expiresAtMs = (cached?.fetched_at ?? 0) + ttlMs;
  const remainingSecs = useCountdown(cached ? expiresAtMs : null);
  const expired = remainingSecs != null && remainingSecs <= 0;

  useEffect(() => {
    if (cached) window.scrollTo(0, 0);
  }, [cached]);

  if (!cached || !resp) {
    return (
      <div className="container" style={{ padding: "60px 0" }}>
        <div className="app-error">
          No hotel search in this session. <a href="/hotels/">Start a new search</a>.
        </div>
      </div>
    );
  }

  const { form } = cached;
  const currency = resp.currency;
  // What the response says served it wins over what we asked for: a partner who
  // switched supplier needs the control to reflect the results on screen, not
  // the request that produced them.
  const activeSource = (resp.provider || form.source || DEFAULT_HOTEL_SOURCE) as HotelSource;

  // Re-run this exact search against another supplier. Rates, room types and
  // availability are all supplier-specific, so this is a new search rather than
  // a filter — hence the full reload once it lands.
  function switchSource(next: HotelSource) {
    reSearch.mutate({ ...form, source: next });
  }

  return (
    <>
      <section className="results-topbar">
        <div className="container topbar-inner">
          <div className="summary">
            <div className="pair">
              <b>{resp.destination.city_name || form.city_name}</b>
              <span>{form.country_name || resp.destination.country_code}</span>
            </div>
            <span className="tag journey hotel-stay">{nightsLabel(resp.nights)}</span>
            <div className="when-pax">
              <b>{fmtDateLong(resp.check_in)}</b> → <b>{fmtDateLong(resp.check_out)}</b>
              &nbsp;·&nbsp; {occupancyLabel(resp.occupancies)}
            </div>
          </div>
          <ProviderSwitch
            sources={HOTEL_SOURCES}
            value={activeSource}
            onChange={switchSource}
            pending={reSearch.isPending ? (reSearch.variables?.source ?? null) : null}
          />
          <CountdownPill targetMs={expiresAtMs} prefix="Rates valid" expiredText="Rates expired" />
          <button type="button" className="modify" onClick={() => nav("/")}>Modify search</button>
        </div>
      </section>

      <div className={`container${expired ? " results-expired" : ""}`}>
        <div className="results-wrap">
          <HotelFilterPanel
            filters={filters}
            onChange={setFilters}
            hotels={hotels}
            facets={resp.facets}
            currency={currency}
          />

          <div>
            {expired && (
              <div className="expired-banner">
                <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                <div className="body">
                  <b>Your rates expired</b>
                  <span>Hotel rates and availability move constantly. Re-run the same search for fresh prices.</span>
                </div>
                <button
                  type="button"
                  className={`btn btn-yellow btn-pill${reSearch.isPending ? " loading" : ""}`}
                  disabled={reSearch.isPending}
                  onClick={() => reSearch.mutate(form)}
                >
                  Retry search
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9M3 3v6h6" /></svg>
                </button>
              </div>
            )}

            {!expired && <KairaTip hotels={hotels} currency={currency} nights={resp.nights} />}

            <HotelSortRow
              sort={sort}
              onChange={setSort}
              stats={resp.sort_stats}
              currency={currency}
              count={`${sorted.length} of ${hotels.length} ${hotels.length === 1 ? "stay" : "stays"}`}
            />

            {hotels.length === 0 ? (
              // The supplier itself returned nothing — a 200 with `hotels: []`
              // and its own message in `detail`. Telling this partner to loosen
              // a filter would send them hunting for a filter that isn't the
              // problem; the other supplier might well have the city.
              <div className="empty-state" style={{ marginTop: 18 }}>
                <span className="emoji">🏨</span>
                <h3>No stays from <span className="serif">{sourceLabel(activeSource)}</span></h3>
                <p>
                  {resp.detail ||
                    `${sourceLabel(activeSource)} has nothing available for these dates.`}
                  {" "}Try another source above, or change your dates.
                </p>
              </div>
            ) : sorted.length === 0 ? (
              <div className="empty-state" style={{ marginTop: 18 }}>
                <span className="emoji">🔍</span>
                <h3>No stays match these <span className="serif">filters</span></h3>
                <p>Loosen a filter to see the rest of this page's {hotels.length} results.</p>
              </div>
            ) : (
              <div className="hotel-list">
                {sorted.map((hotel) => (
                  <HotelResultCard
                    key={hotel.hotel_id}
                    hotel={hotel}
                    currency={currency}
                    nights={resp.nights}
                    rooms={resp.occupancies.length}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── card ───────────────────────────────────────────────────────────────────

const HotelResultCard = memo(function HotelResultCard(props: {
  hotel: HotelCard;
  currency: string;
  nights: number;
  rooms: number;
  onOpen: (hotelId: string) => void;
}) {
  const { hotel } = props;
  const review = reviewLabel(hotel);
  const stars = starLabel(hotel.star_rating);
  const perNight = hotel.pricing.per_night_fare;

  return (
    <article
      className={`hotel-card${hotel.kaira_picked ? " picked" : ""}`}
      onClick={() => props.onOpen(hotel.hotel_id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") props.onOpen(hotel.hotel_id); }}
    >
      <div className="hotel-card-img">
        {hotel.thumbnail || hotel.main_image ? (
          <img src={hotel.thumbnail || hotel.main_image || ""} alt="" loading="lazy" />
        ) : (
          <div className="hotel-card-img-fallback">🏨</div>
        )}
        {hotel.kaira_picked && <span className="hotel-pick-tag">Kaira pick</span>}
      </div>

      <div className="hotel-card-body">
        <div className="hotel-card-head">
          <div>
            <h3>{hotel.name}</h3>
            <div className="hotel-card-loc">
              {stars && <span className="hotel-stars">{stars}</span>}
              {hotel.address || hotel.city_name || ""}
            </div>
          </div>
          {review ? (
            <div className="hotel-score">
              <span className="n">{review.score}</span>
              <span className="w">{review.word}</span>
              <span className="c">{review.count}</span>
            </div>
          ) : (
            // The provider uses 0 as a "no score" sentinel, so an unscored
            // property must not render as 0.0 next to genuinely-rated ones.
            <div className="hotel-score unrated">
              <span className="w">Unrated</span>
              {!!hotel.review_count && (
                <span className="c">{hotel.review_count.toLocaleString("en-IN")} reviews</span>
              )}
            </div>
          )}
        </div>

        <div className="hotel-card-tags">
          <span className={`tag ${hotel.free_breakfast ? "refundable" : "subtle"}`}>
            {fmtBoard(hotel.board_basis, hotel.board_name)}
          </span>
          <span className={`tag ${hotel.refundable ? "refundable" : "non-refundable"}`}>
            {hotel.refundable ? "Free cancellation" : "Non-refundable"}
          </span>
          {hotel.room_type_count > 1 && (
            <span className="tag subtle">
              {hotel.room_type_count}{hotel.room_types_capped ? "+" : ""} room types
            </span>
          )}
        </div>

        {!!hotel.facilities.length && (
          <div className="hotel-card-facilities">
            {hotel.facilities.slice(0, 4).map((f) => <span key={f}>{f}</span>)}
            {hotel.facilities.length > 4 && (
              <span className="more">+{hotel.facilities.length - 4} more</span>
            )}
          </div>
        )}
      </div>

      <div className="hotel-card-price">
        {perNight != null && (
          <div className="per-night">{fmtMoney(perNight, props.currency)}<span> / night</span></div>
        )}
        <div className="total">{fmtMoney(hotel.pricing.final_fare, props.currency)}</div>
        <div className="total-sub">
          total · {nightsLabel(props.nights)}
          {props.rooms > 1 ? ` · ${props.rooms} rooms` : ""}
        </div>
        {!!hotel.pricing.taxes_at_property && (
          // Money the property collects at check-in — quoted so a partner can
          // show a true total, but never part of what we charge.
          <div className="at-property">
            + {fmtMoney(hotel.pricing.taxes_at_property, props.currency)} at property
          </div>
        )}
        <button type="button" className="btn btn-yellow btn-pill btn-sm">
          See rooms
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
        </button>
      </div>
    </article>
  );
});

// ─── filters ────────────────────────────────────────────────────────────────

function HotelFilterPanel(props: {
  filters: Filters;
  onChange: (f: Filters) => void;
  hotels: HotelCard[];
  facets?: HotelSearchResponse["facets"];
  currency: string;
}) {
  const { filters, onChange, facets } = props;

  function toggle<T>(set: Set<T>, value: T): Set<T> {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  }

  const priceMax = facets?.price?.max ?? null;
  const priceMin = facets?.price?.min ?? null;
  const activeCount =
    filters.stars.size + filters.boards.size + filters.facilities.size +
    (filters.refundableOnly ? 1 : 0) + (filters.breakfastOnly ? 1 : 0) +
    (filters.maxPrice != null ? 1 : 0);

  return (
    <aside className="filter-panel">
      <div className="filter-panel-head">
        <b>Filters</b>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => onChange({
              stars: new Set(), boards: new Set(), facilities: new Set(),
              refundableOnly: false, breakfastOnly: false, maxPrice: null,
            })}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Says out loud what these filters can and can't do. The supplier has no
          server-side rate filter, so narrowing here narrows this page only. */}
      <p className="filter-scope-note">Filters apply to the {props.hotels.length} stays on this page.</p>

      {priceMax != null && priceMin != null && priceMax > priceMin && (
        <div className="filter-group">
          <h4>Total price</h4>
          <input
            type="range"
            min={Math.floor(priceMin)}
            max={Math.ceil(priceMax)}
            step={Math.max(Math.round((priceMax - priceMin) / 50), 1)}
            value={filters.maxPrice ?? Math.ceil(priceMax)}
            onChange={(e) => onChange({ ...filters, maxPrice: parseInt(e.target.value, 10) })}
            className="hotel-price-range"
          />
          <div className="filter-range-labels">
            <span>{fmtMoney(priceMin, props.currency)}</span>
            <span>
              up to <b>{fmtMoney(filters.maxPrice ?? priceMax, props.currency)}</b>
            </span>
          </div>
        </div>
      )}

      <div className="filter-group">
        <h4>Terms</h4>
        <label className="filter-row">
          <input
            type="checkbox"
            checked={filters.refundableOnly}
            onChange={() => onChange({ ...filters, refundableOnly: !filters.refundableOnly })}
          />
          <span>Free cancellation</span>
          <span className="count">{props.hotels.filter((h) => h.refundable).length}</span>
        </label>
        <label className="filter-row">
          <input
            type="checkbox"
            checked={filters.breakfastOnly}
            onChange={() => onChange({ ...filters, breakfastOnly: !filters.breakfastOnly })}
          />
          <span>Breakfast included</span>
          <span className="count">{props.hotels.filter((h) => h.free_breakfast).length}</span>
        </label>
      </div>

      {!!facets?.star_ratings?.length && (
        <div className="filter-group">
          <h4>Star rating</h4>
          {[...facets.star_ratings].sort((a, b) => b - a).map((star) => (
            <label className="filter-row" key={star}>
              <input
                type="checkbox"
                checked={filters.stars.has(star)}
                onChange={() => onChange({ ...filters, stars: toggle(filters.stars, star) })}
              />
              <span>{"★".repeat(star)}</span>
              <span className="count">{props.hotels.filter((h) => h.star_rating === star).length}</span>
            </label>
          ))}
        </div>
      )}

      {!!facets?.board_bases?.length && facets.board_bases.length > 1 && (
        <div className="filter-group">
          <h4>Meals</h4>
          {facets.board_bases.map((basis) => (
            <label className="filter-row" key={basis}>
              <input
                type="checkbox"
                checked={filters.boards.has(basis)}
                onChange={() => onChange({ ...filters, boards: toggle(filters.boards, basis) })}
              />
              <span>{fmtBoard(basis)}</span>
              <span className="count">{props.hotels.filter((h) => h.board_basis === basis).length}</span>
            </label>
          ))}
        </div>
      )}

      {!!facets?.facilities?.length && (
        <div className="filter-group">
          <h4>Facilities</h4>
          {facets.facilities.slice(0, 8).map((f) => (
            <label className="filter-row" key={f.name}>
              <input
                type="checkbox"
                checked={filters.facilities.has(f.name)}
                onChange={() => onChange({ ...filters, facilities: toggle(filters.facilities, f.name) })}
              />
              <span>{f.name}</span>
              <span className="count">{f.count}</span>
            </label>
          ))}
        </div>
      )}
    </aside>
  );
}

// ─── sort row ───────────────────────────────────────────────────────────────

function HotelSortRow(props: {
  sort: SortMode;
  onChange: (s: SortMode) => void;
  stats?: HotelSearchResponse["sort_stats"];
  currency: string;
  count: string;
}) {
  const tabs: Array<{ key: SortMode; label: string }> = [
    { key: "cheapest", label: "Cheapest" },
    { key: "top_rated", label: "Top rated" },
    { key: "best_value", label: "Best value" },
  ];
  return (
    <div className="sort-row">
      {tabs.map((tab) => {
        const stat = props.stats?.[tab.key];
        return (
          <button
            key={tab.key}
            type="button"
            className={`sort-tab${props.sort === tab.key ? " active" : ""}`}
            onClick={() => props.onChange(tab.key)}
          >
            <span className="lbl">{tab.label}</span>
            {stat && (
              <span className="val">
                {fmtMoney(stat.fare, props.currency)}
                {stat.review_rating ? ` · ${stat.review_rating.toFixed(1)}` : ""}
              </span>
            )}
          </button>
        );
      })}
      <div className="results-count">{props.count}</div>
    </div>
  );
}

function KairaTip({ hotels, currency, nights }: {
  hotels: HotelCard[]; currency: string; nights: number;
}) {
  const picks = hotels.filter((h) => h.kaira_picked);
  if (!picks.length) return null;
  const cheapest = Math.min(...picks.map((h) => h.pricing.final_fare));
  return (
    <div className="kaira-tip">
      <div className="k-avatar-mini" />
      <div className="copy">
        <b>{picks.length === 1 ? picks[0].name : `${picks.length} picks`}</b> — well reviewed and
        priced below this page's median, from <b>{fmtMoney(cheapest, currency)}</b> for {nightsLabel(nights)}.
      </div>
    </div>
  );
}

// ─── filtering / sorting ────────────────────────────────────────────────────

function applyFilters(hotels: HotelCard[], f: Filters): HotelCard[] {
  return hotels.filter((h) => {
    if (f.refundableOnly && !h.refundable) return false;
    if (f.breakfastOnly && !h.free_breakfast) return false;
    if (f.stars.size && !(h.star_rating && f.stars.has(h.star_rating))) return false;
    if (f.boards.size && !f.boards.has(String(h.board_basis))) return false;
    if (f.maxPrice != null && h.pricing.final_fare > f.maxPrice) return false;
    if (f.facilities.size) {
      const owned = new Set(h.facilities);
      for (const wanted of f.facilities) if (!owned.has(wanted)) return false;
    }
    return true;
  });
}

function sortHotels(hotels: HotelCard[], sort: SortMode): HotelCard[] {
  const list = [...hotels];
  if (sort === "cheapest") {
    list.sort((a, b) => a.pricing.final_fare - b.pricing.final_fare);
  } else if (sort === "top_rated") {
    // Unrated properties sink rather than sorting as zero — 0 is the provider's
    // "no score" sentinel, not a bad review.
    list.sort((a, b) => (b.review_rating ?? -1) - (a.review_rating ?? -1)
      || a.pricing.final_fare - b.pricing.final_fare);
  } else {
    list.sort((a, b) =>
      (b.review_rating ?? 0) / Math.max(b.pricing.final_fare, 1) -
      (a.review_rating ?? 0) / Math.max(a.pricing.final_fare, 1));
  }
  return list;
}
